/**
 * PRODUCTS-WORKSPACE-000001 — product summary mapped onto framework strip.
 * Honest full-catalog status KPIs only when page holds entire result set.
 */
import { WorkspaceSummaryStrip, formatHonestCount, type SummaryMetric } from '../../../commerce-workspace';
import type { ProductSummaryCounts } from './productStatus';

export function ProductSummaryStrip({
  summary,
  lastSync,
  source,
}: {
  summary: ProductSummaryCounts;
  lastSync?: string | null;
  source?: string | null;
}) {
  const metrics: SummaryMetric[] = [
    {
      id: 'total',
      label: 'Total products',
      value: formatHonestCount(summary.totalDb),
      hint: 'Database match count',
      emphasis: true,
    },
    {
      id: 'active',
      label: 'Active',
      value: formatHonestCount(summary.active),
      hint: summary.fullCatalogKnown
        ? 'BUYABLE / DISCOVERABLE (this catalog filter set)'
        : 'Full-catalog N/A · status filter or page snapshot below',
    },
    {
      id: 'suppressed',
      label: 'Suppressed',
      value: formatHonestCount(summary.suppressed),
      hint: summary.fullCatalogKnown
        ? 'NOT_DISCOVERABLE / DELETED-like'
        : 'Full-catalog N/A until entire result fits one page',
    },
    {
      id: 'oos',
      label: 'Out of stock',
      value: formatHonestCount(summary.oos),
      hint: summary.fullCatalogKnown
        ? 'Inventory field present and 0'
        : 'Requires inventory field on each row + full page',
    },
    {
      id: 'attention',
      label: 'Needs attention',
      value: formatHonestCount(summary.attention),
      hint: summary.fullCatalogKnown
        ? 'Missing ASIN, title, or status'
        : 'Full-catalog N/A · see page snapshot',
    },
    {
      id: 'displayed',
      label: 'Displayed',
      value: formatHonestCount(summary.displayed),
      hint:
        summary.amazonCount != null
          ? `Amazon pull hint ${summary.amazonCount.toLocaleString()}`
          : 'Amazon count N/A until sync health reports',
    },
  ];

  const pageHint = summary.fullCatalogKnown
    ? 'Status buckets computed from this full result set (all matching products fit on one page).'
    : `This page · ${summary.pageActive} active / ${summary.pageSuppressed} suppressed / ${summary.pageOos} OOS / ${summary.pageAttention} attention. Status totals above stay N/A so we never invent catalog-wide KPIs.`;

  return (
    <WorkspaceSummaryStrip
      title="Catalog at a glance"
      ariaLabel="Product catalog summary"
      metrics={metrics}
      source={source}
      lastSync={lastSync}
      note={pageHint}
    />
  );
}
